<template>
  <i-modal
    :title="openedLink.isNew ? __('Insert Link') : __('Edit Link')"
    :style="{ maxWidth: '400px' }"
    :closeOnClickAway="false"
    @openAutoFocus="syncLink"
    v-model:show="show"
    @keydown.enter.prevent="addLink(() => emit('update:show', false))"
  >
    <template #default>
      <div class="tabs">
        <button @click="activeTab = 'link'" :class="{ active: activeTab === 'link' }">
          {{ __('General') }}
        </button>
        <button @click="activeTab = 'advanced'" :class="{ active: activeTab === 'advanced' }">
          {{ __('Advanced') }}
        </button>
      </div>

      <form class="modal-form">
        <!-- General Tab -->
        <div v-if="activeTab === 'link'" class="tab-content">
          <div class="form-grid">
            <div class="link-group">
              <label>{{ __('Link type:') }}</label>
              <Dropdown :item-count="linkTypes.length">
                <template #trigger="{ toggle, attrs }">
                  <span class="i-dropdown-wrapper">
                    <i-button class="link-modal-dropdown" @click.prevent="toggle" v-bind="attrs">
                      <span class="tiptap-select-label">{{
                        activeLabel(openedLink.type, linkTypes)?.label
                      }}</span>
                      <i-icon
                        icon="caret-down"
                        :style="{ fontSize: '0.6em' }"
                        :title="__('More options')"
                      />
                    </i-button>
                  </span>
                </template>
                <template #default="{ isOpen, close, activeIndex }">
                  <ul class="dropdown-menu i-dropdown-menu block" role="select">
                    <div class="pl2">{{ __('Select the type of link') }}</div>
                    <li
                      v-for="(item, index) in linkTypes"
                      :key="index"
                      :class="[
                        'dropdown-item',
                        { 'i-dropdown-item-selected': item.value === openedLink.type }
                      ]"
                      :aria-label="item.label"
                      v-dropdown-focus="{ index, activeIndex }"
                    >
                      <a
                        href="#"
                        class="i-action-browse-by"
                        :title="item.label"
                        @click.prevent="selectLinkType(item, close)"
                        @keydown.enter.prevent.stop="selectLinkType(item, close)"
                      >
                        {{ item.label }}
                      </a>
                    </li>
                  </ul>
                </template>
              </Dropdown>

              <!-- URL type -->
              <template v-if="openedLink.type === 'URL'">
                <label>{{ __('Target') }}</label>
                <div class="target-group">
                  <Dropdown :item-count="linkTargets.length">
                    <template #trigger="{ toggle, attrs }">
                      <span class="i-dropdown-wrapper">
                        <i-button
                          class="link-modal-dropdown"
                          @click.prevent="toggle"
                          v-bind="attrs"
                        >
                          <span class="tiptap-select-label">{{
                            activeLabel(openedLink.attr.target, linkTargets)?.label ||
                            openedLink.attr.target
                          }}</span>
                          <i-icon
                            icon="caret-down"
                            :style="{ fontSize: '0.6em' }"
                            :title="__('More options')"
                          />
                        </i-button>
                      </span>
                    </template>
                    <template #default="{ isOpen, close, activeIndex }">
                      <ul class="dropdown-menu i-dropdown-menu block" role="select">
                        <div class="pl2">{{ __('Select the link target') }}</div>
                        <li
                          v-for="(item, index) in linkTargets"
                          :key="index"
                          :class="[
                            'dropdown-item',
                            { 'i-dropdown-item-selected': item.value === openedLink.attr.target }
                          ]"
                          :aria-label="item.label"
                          v-dropdown-focus="{ index, activeIndex }"
                          >
                          <a
                            href="#"
                            class="i-action-browse-by"
                            :title="item.label"
                            @click.prevent="selectLinkType(item, close)"
                            @keydown.enter.prevent.stop="selectLinkType(item, close)"
                          >
                            {{ item.label }}
                          </a>
                        </li>
                      </ul>
                    </template>
                  </Dropdown>
                  <input
                    v-if="
                      !activeLabel(openedLink.attr.target, linkTargets) ||
                      activeLabel(openedLink.attr.target, linkTargets)?.value === ''
                    "
                    v-model.trim="openedLink.attr.target"
                    autocomplete="off"
                  />
                </div>

                <label>{{ __('Protocol') }}</label>
                <Dropdown :item-count="protocols.length">
                  <template #trigger="{ toggle, attrs }">
                    <span class="i-dropdown-wrapper">
                      <i-button class="link-modal-dropdown" @click.prevent="toggle" v-bind="attrs">
                        <span class="tiptap-select-label">{{
                          activeLabel(openedLink.protocol, protocols)?.label
                        }}</span>
                        <i-icon
                          icon="caret-down"
                          :style="{ fontSize: '0.6em' }"
                          :title="__('More options')"
                        />
                      </i-button>
                    </span>
                  </template>
                  <template #default="{ isOpen, close, activeIndex }">
                    <ul class="dropdown-menu i-dropdown-menu block" role="select">
                      <div class="pl2">{{ __('Select the protocol') }}</div>
                      <li
                        v-for="(item, index) in protocols"
                        :key="index"
                        :class="[
                          'dropdown-item',
                          { 'i-dropdown-item-selected': item.label === openedLink.protocol }
                        ]"
                        :aria-label="item.label"
                        v-dropdown-focus="{ index, activeIndex }"
                      >
                        <a
                          href="#"
                          class="i-action-browse-by"
                          :title="item.label"
                          @click.prevent="selectProtocol(item, close)"
                          @keydown.enter.prevent.stop="selectProtocol(item, close)"
                        >
                          {{ item.label }}
                        </a>
                      </li>
                    </ul>
                  </template>
                </Dropdown>

                <label>{{ __('Link text') }}</label>
                <input ref="firstInput" v-model.trim="openedLink.text" autocomplete="off" />

                <label>{{ __('URL') }}</label>
                <input
                  v-model.trim="openedLink.href"
                  @blur="setOpenedLinkProps({ href: openedLink.href })"
                  autocomplete="off"
                />
              </template>

              <!-- Email type -->
              <template v-if="openedLink.type === 'Email'">
                <label>{{ __('Link text') }}</label>
                <input v-model.trim="openedLink.text" autocomplete="off" />
                <label>{{ __('Email') }}</label>
                <input type="email" v-model.trim="openedLink.email.email" autocomplete="off" />
                <label>{{ __('Subject') }}</label>
                <input v-model.trim="openedLink.email.subject" autocomplete="off" />
                <label>{{ __('Body') }}</label>
                <input v-model.trim="openedLink.email.body" autocomplete="off" />
              </template>

              <!-- Telephone type -->
              <template v-if="openedLink.type === 'Telephone'">
                <label>{{ __('Link text') }}</label>
                <input v-model.trim="openedLink.text" autocomplete="off" />
                <label>{{ __('Telephone') }}</label>
                <input v-model.trim="openedLink.phone" autocomplete="off" />
              </template>

              <!-- Anchor type -->
              <template v-if="openedLink.type === 'Anchor'">
                <label>{{ __('Target') }}</label>
                <Dropdown :item-count="linkTargets.length">
                  <template #trigger="{ toggle, attrs }">
                    <span class="i-dropdown-wrapper">
                      <i-button class="link-modal-dropdown" @click.prevent="toggle" v-bind="attrs">
                        <span class="tiptap-select-label">{{
                          activeLabel(openedLink.attr.target, linkTargets)?.label ||
                          openedLink.attr.target
                        }}</span>
                        <i-icon
                          icon="caret-down"
                          :style="{ fontSize: '0.6em' }"
                          :title="__('More options')"
                        />
                      </i-button>
                    </span>
                  </template>
                  <template #default="{ isOpen, close, activeIndex }">
                    <ul class="dropdown-menu i-dropdown-menu block" role="select">
                      <div class="pl2">{{ __('Select the link target') }}</div>
                      <li
                        v-for="(item, index) in linkTargets"
                        :key="index"
                        :class="[
                          'dropdown-item',
                          { 'i-dropdown-item-selected': item.value === openedLink.attr.target }
                        ]"
                        :aria-label="item.label"
                        v-dropdown-focus="{ index, activeIndex }"
                      >
                        <a
                          href="#"
                          class="i-action-browse-by"
                          :title="item.label"
                          @click.prevent="selectLinkType(item, close)"
                          @keydown.enter.prevent.stop="selectLinkType(item, close)"
                        >
                          {{ item.label }}
                        </a>
                      </li>
                    </ul>
                  </template>
                </Dropdown>
                <input
                  v-if="
                    !activeLabel(openedLink.attr.target, linkTargets) ||
                    activeLabel(openedLink.attr.target, linkTargets)?.value === ''
                  "
                  v-model.trim="openedLink.attr.target"
                  autocomplete="off"
                />
                <label>{{ __('Link text') }}</label>
                <input v-model.trim="openedLink.text" autocomplete="off" />
                <label>{{ __('URL') }}</label>
                <input
                  v-model.trim="openedLink.href"
                  @blur="setOpenedLinkProps({ href: openedLink.href })"
                  autocomplete="off"
                />
              </template>
            </div>
          </div>

          <div v-if="openedLink.type === 'URL'" class="button-group">
            <button 
              class="btn-action" 
              @click.prevent="callInsertionWindow('link')"
              @keydown.enter.prevent.stop="callInsertionWindow('link')"
            >
              <i-icon :icon="`folder`" type="far" size="small" />
              {{ __('Browse MAM') }}
            </button>
            <button
              class="btn-action"
              @click.prevent="callInsertionWindow('link', '/document/browser')"
              @keydown.enter.prevent.stop="callInsertionWindow('link', '/document/browser')"
            >
              <i-icon :icon="`link`" type="far" size="small" />
              {{ __('Imperia Link') }}
            </button>
            <button 
              class="btn-action right" 
              @click.prevent="resetOpenedLink"
              @keydown.enter.prevent.stop="resetOpenedLink"
              >
              <i-icon :icon="`refresh`" type="far" size="small" />
              {{ __('Unset link') }}
            </button>
          </div>
        </div>

        <!-- Advanced Tab -->
        <div v-if="activeTab === 'advanced'" class="tab-content">
          <div class="form-grid">
            <div class="link-group">
              <label>{{ __('ID') }}</label>
              <input type="text" v-model.trim="openedLink.attr.id" autocomplete="off" />

              <label>{{ __('Name') }}</label>
              <input type="text" v-model.trim="openedLink.attr.name" autocomplete="off" />

              <label>{{ __('Class') }}</label>
              <input type="text" v-model.trim="openedLink.attr.class" autocomplete="off" />

              <label>{{ __('Long Description') }}</label>
              <input type="text" v-model.trim="openedLink.attr.longdesc" autocomplete="off" />

              <label>{{ __('Advisory Title') }}</label>
              <input type="text" v-model.trim="openedLink.attr.title" autocomplete="off" />

              <label>{{ __('Style') }}</label>
              <input type="text" v-model.trim="openedLink.attr.style" autocomplete="off" />

              <label>{{ __('Rel Attribute') }}</label>
              <div class="checkbox-group">
                <label><input type="checkbox" value="nofollow" v-model="rel" /> Nofollow</label>
                <label><input type="checkbox" value="noopener" v-model="rel" /> Noopener</label>
                <label><input type="checkbox" value="noreferrer" v-model="rel" /> Noreferrer</label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </template>

    <template #footer>
      <div>
        <i-button 
          variant="secondary" 
          @click.prevent="emit('update:show', false)"  
          @keydown.enter.prevent.stop="emit('update:show', false)"
        >
          {{__('Cancel')}}
        </i-button>
        <i-button variant="primary" @click.prevent="addLink(() => emit('update:show', false))">
          {{ openedLink.isNew ? __('Insert Link') : __('Apply Changes') }}
        </i-button>
      </div>
    </template>
  </i-modal>
</template>

<script setup>
import { ref, shallowRef, useAttrs, watch } from 'vue'
import Dropdown from '@/components/lib/dropdown/Dropdown.vue'
import { useMamTiptap } from '@/composables/useMamTiptap'

const emit = defineEmits(['update:show'])
const props = defineProps({ 
  editor: { type: Object, required: true },
})
const editorRef = shallowRef(props.editor)
const show = defineModel('show', { type: Boolean, default: false })

const mamLinkExt = editorRef.value.extensionManager.extensions.find(ext => ext.name === 'link')
const mamLinkOptions = mamLinkExt?.options

const {
  __,
  openedLink,
  linkTargets,
  linkTypes,
  protocols,
  activeLabel,
  setOpenedLinkProps,
  callInsertionWindow,
  addLink,
  getEditorLink,
  resetOpenedLink
} = useMamTiptap(editorRef)

const firstInput = ref(null)
const activeTab = ref('link')
const rel = ref([])

// keep rel in sync with openedLink.attr.rel
watch(rel, (vals) => {
  openedLink.attr.rel = (vals || []).filter(Boolean).join(' ')
})

const attrs = useAttrs()
watch(
  () => attrs.show,
  (val) => {
    if (val) syncLink()
  }
)

function selectLinkType(item, close) {
  item.method()
  close()
}
function selectProtocol(item, close) {
  item.method()
  close()
}

function syncLink() {
  activeTab.value = 'link'
  if (firstInput?.value?.focus) {
    firstInput.value.focus();
  }

  if (editorRef.value.isActive('link')) {
    editorRef.value.chain().focus().extendMarkRange('link').run()
  }
  getEditorLink()
  // initialize rel from existing mark or schema default
  const existing = openedLink.attr.rel
  if (existing) {
    rel.value = existing.split(' ').filter(Boolean)
  } else {
    const defRel = editorRef.value?.schema?.marks?.link?.attrs?.rel?.default
    if (defRel) {
      rel.value = defRel.split(' ').filter(Boolean)
      openedLink.attr.rel = defRel
    } else {
      rel.value = []
      openedLink.attr.rel = ''
    }

    if (mamLinkOptions) {
      openedLink.protocol = mamLinkOptions?.defaultProtocol;
    }
  }
}
</script>

<style scoped>
.tiptap-select-label {
  margin-right: 5px;
}
.i-dropdown-menu {
  display: block;
  overflow-y: auto;
  max-height: 200px;
  width: 120px;
}
.i-dropdown-menu .pl2 {
  width: 100%;
  padding: 4px;
  padding-left: 14px;
  background-color: lightgray;
  border-bottom: 1px solid gray;
}
.tabs {
  display: flex;
  justify-content: flex-start;
  border-bottom: 1px solid #e5e5e5;
}
.tabs button {
  background: none;
  border: none;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  color: #333;
}
.tabs button.active {
  font-weight: bold;
  border-bottom: 2px solid #007bff;
  color: #007bff;
}
.tabs button:hover {
  background-color: #e1e1e1;
}
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.form-grid {
  display: flex;
  margin-top: 5px;
}
.form-grid label {
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 2px;
}
.form-grid input {
  width: auto;
  padding: 8px;
  font-size: 12px;
  border: 1px solid #d1d5db;
  border-radius: 3px;
}
.link-group {
  width: 100%;
  display: grid;
  grid-template: auto auto / 100px auto;
  font-size: 12px;
  gap: 5px;
}
.link-group label {
  margin: auto;
  margin-left: 0px;
}
.button-group {
  width: 100%;
  margin-top: 15px;
  display: flex;
  gap: 5px;
}
.btn-action {
  flex: 1;
  padding: 4px 8px;
  font-size: 13px;
  cursor: pointer;
  background-color: white;
  color: black;
  border: 1px solid #d1d5db;
  border-radius: 3px;
}
.btn-action:hover {
  background-color: #f3f4f6;
  border-color: #cbd5e1;
}
.link-modal-dropdown {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0px;
  font-size: 14px;
  background-color: white;
  color: #111827;
  border: 1px solid #d1d5db;
  border-radius: 3px !important;
  cursor: pointer;
  text-align: left;
}
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
}
.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  font-weight: 400;
  cursor: pointer;
}
.checkbox-group input[type='checkbox'] {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
  cursor: pointer;
  margin: 0;
}
</style>
